import { LazyQueryHookOptions, useLazyQuery } from '@apollo/client'
import getCheckoutRuleQuery from './query'
import {
  IGetCheckoutRuleResponse,
  IGetCheckoutRuleVariables,
} from './types'

/**
 * Lazy, so a screen fetches on mount rather than on every render.
 *
 * **`no-cache` is a pin, not a default, and the key order below is
 * load-bearing.** Caching here would be wrong rather than merely stale:
 * `DELIVERY_DATES` is computed from the clock at request time and filtered to
 * days strictly after today, so a cached answer goes wrong at midnight in the
 * storefront's timezone with nothing to invalidate it. The variables are the
 * storefront, the rule type and the session — all constant for a session — so
 * Apollo would key every fetch identically and serve the first answer for the
 * rest of it.
 *
 * The policy options are omitted from the parameter so the pin cannot be
 * defeated from a call site: leaving them in meant `fetchPolicy` still
 * compiled and was merely ignored, and `nextFetchPolicy` /
 * `initialFetchPolicy` would still have won outright. Apollo lets the
 * *execute* call override the policy as well, which a wrapper cannot prevent
 * without changing what it returns — nothing does that here. Same shape as
 * `linkedProducts/hook.ts`, for the same class of reason.
 *
 * @param options: LazyQueryHookOptions without the fetch-policy keys
 * @internal
 */
const useLazyGetCheckoutRule = (
  options?: Omit<
    LazyQueryHookOptions<IGetCheckoutRuleResponse, IGetCheckoutRuleVariables>,
    'fetchPolicy' | 'nextFetchPolicy' | 'initialFetchPolicy'
  >,
) => {
  return useLazyQuery<IGetCheckoutRuleResponse, IGetCheckoutRuleVariables>(
    getCheckoutRuleQuery,
    {
      ...options,
      fetchPolicy: 'no-cache',
    },
  )
}

export { useLazyGetCheckoutRule }
